/**
 * Wave 2.5 — merge clause-dictionary entries into comprehensive clause categories.
 * NEVER removes or replaces existing hardcoded residential clauses.
 */

import type { ClauseDictionaryEntry } from '@/apis/extended/clauseDictionaryApi';
import type {
  ComprehensiveClauseCategory,
  ComprehensiveClauseData,
} from '@/utils/comprehensiveClauseMapping';

function mapValidationType(
  t?: string
): NonNullable<ComprehensiveClauseData['validation']>['type'] {
  switch (t) {
    case 'number':
    case 'currency':
    case 'percentage':
    case 'date':
    case 'multiline':
    case 'boolean':
    case 'text':
      return t;
    default:
      return 'text';
  }
}

function toComprehensive(
  entry: ClauseDictionaryEntry,
  contractId: string
): ComprehensiveClauseData {
  return {
    id: entry.clauseId,
    hierarchicalId: `C-${contractId}-DICT-${entry.clauseId}`,
    title: entry.title,
    description: entry.description || entry.title,
    value: entry.defaultValue ?? '',
    originalValue: entry.defaultValue ?? '',
    category: 'other',
    subcategory: entry.subcategory || 'dictionary',
    isNegotiable: entry.isNegotiable !== false,
    isRequired: Boolean(entry.isRequired || entry.requiredByState),
    source: 'clauseDictionary',
    validation: {
      type: mapValidationType(entry.validation?.type),
      required: entry.validation?.required ?? entry.isRequired,
      options: entry.validation?.options,
    },
  };
}

/**
 * Append dictionary clauses as an extra category.
 * Existing categories and clauses are left untouched.
 */
export function mergeDictionaryIntoCategories(
  base: ComprehensiveClauseCategory[],
  dictionary: ClauseDictionaryEntry[],
  contractId: string
): ComprehensiveClauseCategory[] {
  if (!dictionary?.length) return base;

  const existingIds = new Set(
    base.flatMap((cat) => cat.clauses.map((c) => c.id))
  );

  const fresh = dictionary
    .filter((d) => d.status !== 'archived' && !existingIds.has(d.clauseId))
    .map((d) => toComprehensive(d, contractId));

  if (!fresh.length) return base;

  const dictCategory: ComprehensiveClauseCategory = {
    id: 'dictionary-commercial',
    title: 'Commercial / dictionary clauses',
    description:
      'State packs and custom clauses from the clause dictionary (additive).',
    clauses: fresh,
    isCollapsible: true,
    isExpanded: true,
    subcategories: ['dictionary'],
  };

  return [...base, dictCategory];
}
